import { useId, useState } from "react";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import {
  PsvMonitoringProgramStatus,
  STANDARD_RULE_NAME,
} from "@api/shared-runtime/psv-monitoring";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ProviderPoolSelect } from "@/components/Selects/provider-pool-select";
import { ProviderSharedSelect } from "@/components/Selects/ProviderSharedSelect";
import { useTRPC } from "@/trpc";
import { DiscardChangesDialog } from "../discard-changes-dialog";
import { ImpactSummary } from "../impact-summary";
import { MONITORING_COPY, PREVIEW_DEBOUNCE_MS } from "../monitoring-constants";
import {
  describeMissingStates,
  isConflictError,
  providerIdLabel,
} from "../monitoring-format";
import {
  allSourcesDraft,
  familiesMissingStates,
  toSourceDraft,
} from "../monitoring-sources";
import type {
  MonitoringProgram,
  ProgramDraft,
  SourceDraft,
} from "../monitoring-types";
import { SourceSelector } from "../source-selector";
import { useDebouncedValue } from "../use-debounced-value";
import { useInvalidateMonitoring } from "../use-monitoring-invalidation";

type NamedId = { name: string; id: string };

const MAX_RULE_NAME_LENGTH = 100;

type Props = {
  organizationBusinessUnitId: string;
  /** The rule being edited; null creates one. */
  program: MonitoringProgram | null;
  /** Creating the standard rule from the empty page prefills its name. */
  startAsStandard?: boolean;
  onClose: () => void;
  /** Reloads the saved rules and reopens the editor on the latest version. */
  onReload: () => void;
};

/** The first requirement the draft misses, shown beside Save so a disabled
 * button says why; null when it can be saved. A paused rule may be saved
 * while it still has no checks or targets. */
function ruleSaveBlocker({
  name,
  sources,
  hasTargets,
  needsCompleteRule,
}: {
  name: string;
  sources: SourceDraft[];
  hasTargets: boolean;
  needsCompleteRule: boolean;
}): string | null {
  if (name.length === 0) {
    return MONITORING_COPY.nameRequired;
  }
  const missingStates = familiesMissingStates(sources);
  if (missingStates.length > 0) {
    return describeMissingStates(missingStates);
  }
  if (!needsCompleteRule) {
    return null;
  }
  if (sources.length === 0) {
    return MONITORING_COPY.sourceRequired;
  }
  if (!hasTargets) {
    return MONITORING_COPY.targetRequired;
  }
  return null;
}

function initialName({
  program,
  startAsStandard,
}: Pick<Props, "program" | "startAsStandard">): string {
  if (program) {
    return program.name;
  }
  return startAsStandard ? STANDARD_RULE_NAME : "";
}

export function RuleEditorSheet({
  organizationBusinessUnitId,
  program,
  startAsStandard = false,
  onClose,
  onReload,
}: Props) {
  const trpc = useTRPC();
  const invalidate = useInvalidateMonitoring();
  const [name, setName] = useState(() =>
    initialName({ program, startAsStandard }),
  );
  const [sources, setSources] = useState(() =>
    program ? program.sources.map(toSourceDraft) : allSourcesDraft(),
  );
  const [pools, setPools] = useState<NamedId[]>(
    () => program?.targets.providerPools ?? [],
  );
  const [providers, setProviders] = useState<NamedId[]>(() =>
    (program?.targets.providers ?? []).map((provider) => ({
      id: provider.id,
      name: provider.name ?? providerIdLabel(provider.id),
    })),
  );
  const [isStale, setIsStale] = useState(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const nameId = useId();

  const draft: ProgramDraft = {
    name: name.trim(),
    sources,
    targets: {
      providerPoolIds: pools.map((pool) => pool.id),
      providerUserIds: providers.map((provider) => provider.id),
    },
  };
  const [initialDraftKey] = useState(() => JSON.stringify(draft));
  const hasTargets =
    draft.targets.providerPoolIds.length +
      draft.targets.providerUserIds.length >
    0;
  const status = program?.status ?? PsvMonitoringProgramStatus.Active;
  // A paused rule may be saved while it is still being set up.
  const needsCompleteRule = status === PsvMonitoringProgramStatus.Active;
  const saveBlocker = ruleSaveBlocker({
    name: draft.name,
    sources,
    hasTargets,
    needsCompleteRule,
  });
  const isDraftValid = saveBlocker === null;

  // Debounce a string: `draft` is a new object every render, so debouncing it
  // directly would restart the timer on every render and never settle.
  const draftKey = JSON.stringify(draft);
  const isDirty = draftKey !== initialDraftKey;
  const debouncedKey = useDebouncedValue({
    value: draftKey,
    delayMs: PREVIEW_DEBOUNCE_MS,
  });
  const previewQuery = useQuery({
    ...trpc.psv.monitoring.previewProgramEdit.queryOptions({
      organizationBusinessUnitId,
      programId: program?.id,
      draft: JSON.parse(debouncedKey) as ProgramDraft,
      status,
    }),
    enabled: isDraftValid && debouncedKey === draftKey,
    placeholderData: keepPreviousData,
  });
  const isPreviewPending =
    isDraftValid && (debouncedKey !== draftKey || previewQuery.isFetching);

  const create = useMutation(
    trpc.psv.monitoring.createProgram.mutationOptions(),
  );
  const update = useMutation(
    trpc.psv.monitoring.updateProgram.mutationOptions(),
  );
  const saveError = create.error ?? update.error;
  const isSaving = create.isPending || update.isPending;

  function requestClose() {
    if (isDirty && !isSaving) {
      setConfirmingDiscard(true);
      return;
    }
    onClose();
  }

  async function save() {
    try {
      if (program) {
        await update.mutateAsync({
          organizationBusinessUnitId,
          programId: program.id,
          expectedRevision: program.revision,
          draft,
        });
      } else {
        await create.mutateAsync({
          organizationBusinessUnitId,
          draft,
          status: PsvMonitoringProgramStatus.Active,
          isStandard: startAsStandard,
        });
      }
    } catch (error) {
      // The draft stays visible so it can be compared with the latest rule.
      setIsStale(isConflictError(error));
      return;
    }
    await invalidate();
    toast.success("Rule saved.");
    onClose();
  }

  return (
    <Sheet open onOpenChange={(open) => !open && requestClose()}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-xl">
        <SheetHeader className="border-b p-4 pr-16 sm:p-6 sm:pr-16">
          <SheetTitle>
            {program ? "Edit rule" : "New monitoring rule"}
          </SheetTitle>
          <SheetDescription>
            {MONITORING_COPY.editorDescription}
          </SheetDescription>
        </SheetHeader>
        <div className="flex-1 space-y-6 overflow-y-auto p-4 sm:p-6">
          {isStale && (
            <Alert role="alert">
              <AlertDescription className="flex flex-wrap items-center gap-2">
                {MONITORING_COPY.staleRule}
                <Button variant="outline" size="sm" onClick={onReload}>
                  Reload
                </Button>
              </AlertDescription>
            </Alert>
          )}
          <div className="space-y-1">
            <Label htmlFor={nameId}>Name</Label>
            <Input
              id={nameId}
              value={name}
              maxLength={MAX_RULE_NAME_LENGTH}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <section className="space-y-2">
            <h3 className="text-sm font-medium">What to check</h3>
            <SourceSelector
              value={sources}
              onChange={setSources}
              coverageGaps={previewQuery.data?.scope.coverageGaps}
            />
          </section>
          <section className="space-y-3">
            <h3 className="text-sm font-medium">Who to monitor</h3>
            <div className="space-y-1">
              <Label>Provider pools</Label>
              <ProviderPoolSelect
                isMulti
                value={pools}
                onChange={setPools}
                placeholder="Add provider pools"
              />
            </div>
            <div className="space-y-1">
              <Label>Individual providers</Label>
              <ProviderSharedSelect
                isMulti
                value={providers}
                onChange={(next) =>
                  setProviders(Array.isArray(next) ? next : [])
                }
                organizationBusinessUnitId={organizationBusinessUnitId}
                placeholder="Add providers"
              />
            </div>
            {needsCompleteRule && !hasTargets && (
              <p className="text-xs text-muted-foreground">
                {MONITORING_COPY.targetRequired}
              </p>
            )}
          </section>
          {isDraftValid && (
            <ImpactSummary
              preview={previewQuery.data}
              isPending={isPreviewPending}
            />
          )}
          {saveError && !isStale && (
            <Alert role="alert">
              <AlertDescription>{saveError.message}</AlertDescription>
            </Alert>
          )}
        </div>
        <SheetFooter className="gap-2 border-t p-4 sm:items-center sm:p-6">
          {saveBlocker && (
            <p role="status" className="text-sm font-medium sm:mr-auto">
              {saveBlocker}
            </p>
          )}
          <Button variant="outline" onClick={requestClose}>
            Cancel
          </Button>
          <Button
            disabled={!isDraftValid || isSaving || isStale}
            onClick={() => void save()}
          >
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save rule
          </Button>
        </SheetFooter>
      </SheetContent>
      {confirmingDiscard && (
        <DiscardChangesDialog
          onKeepEditing={() => setConfirmingDiscard(false)}
          onDiscard={onClose}
        />
      )}
    </Sheet>
  );
}
